// Composite "feels-good" index combining temperature, humidity, wind chill,
// AQI, and UV. Returns 0-100 with a label.

export interface ComfortInput {
  tempC: number;
  feelsLikeC: number;
  humidity: number;
  windMs: number;
  uvi: number;
  aqi?: number; // 1..5
}

export interface ComfortIndex {
  score: number; // 0-100
  label: "Excellent" | "Good" | "Fair" | "Poor" | "Hazardous";
  color: "emerald" | "lime" | "amber" | "orange" | "red";
  reasons: string[];
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

export function comfortIndex(input: ComfortInput): ComfortIndex {
  const reasons: string[] = [];

  // Thermal comfort: peak around 20°C feels-like.
  const t = input.feelsLikeC;
  let thermal = 100 - Math.abs(t - 20) * 4;
  thermal = clamp(thermal, 0, 100);
  if (t < 5) reasons.push("cold");
  else if (t > 30) reasons.push("hot");
  else if (t < 12) reasons.push("cool");
  else if (t > 26) reasons.push("warm");

  // Humidity: muggy or arid penalty
  const h = input.humidity;
  let humid = 100;
  if (h > 70) {
    humid = 100 - (h - 70) * 2;
    if (h > 85) reasons.push("muggy");
  } else if (h < 25) {
    humid = 100 - (25 - h) * 2;
    reasons.push("dry air");
  }
  humid = clamp(humid, 0, 100);

  // Wind: comfortable up to 5 m/s, then chips off.
  const w = input.windMs;
  let wind = w <= 5 ? 100 : 100 - (w - 5) * 6;
  wind = clamp(wind, 0, 100);
  if (w > 9) reasons.push("windy");

  // UV: high UV without sun protection is uncomfortable.
  let uvScore = 100;
  if (input.uvi > 5) {
    uvScore = 100 - (input.uvi - 5) * 8;
    if (input.uvi > 8) reasons.push("very high UV");
    else reasons.push("high UV");
  }
  uvScore = clamp(uvScore, 0, 100);

  // AQI: 1=best, 5=worst.
  let air = 100;
  if (input.aqi != null) {
    if (input.aqi === 2) air = 88;
    else if (input.aqi === 3) {
      air = 65;
      reasons.push("moderate air");
    } else if (input.aqi === 4) {
      air = 35;
      reasons.push("poor air");
    } else if (input.aqi >= 5) {
      air = 10;
      reasons.push("hazardous air");
    }
  }

  // Weighted blend.
  const score = Math.round(
    thermal * 0.35 + humid * 0.15 + wind * 0.15 + uvScore * 0.1 + air * 0.25,
  );

  const label: ComfortIndex["label"] =
    score >= 85
      ? "Excellent"
      : score >= 70
        ? "Good"
        : score >= 50
          ? "Fair"
          : score >= 30
            ? "Poor"
            : "Hazardous";

  const color: ComfortIndex["color"] =
    score >= 85
      ? "emerald"
      : score >= 70
        ? "lime"
        : score >= 50
          ? "amber"
          : score >= 30
            ? "orange"
            : "red";

  if (reasons.length === 0) reasons.push("pleasant overall");

  return { score, label, color, reasons };
}
